import Vue from 'vue';
import VueRouter from 'vue-router';
import cookie from 'vue-cookies';
import store from '@/store';
import VueMeta from 'vue-meta';
import Connection from '@/views/errors/Connection';
import { USER_TYPE } from '@/utils/const';
import guardPortalAccess from '@/utils/routeGuards';

const AUTH_AUTH_GETTER = 'auth/isAuthenticated';
const AGENT_DASHBOARD = '/agent-dashboard';
const CUSTOMER_DASHBOARD = '/dashboard';

Vue.use(VueMeta);

Vue.use(VueRouter);

const ifNotAuthenticated = (_to, _from, next) => {
  if (!store.getters[AUTH_AUTH_GETTER]) {
    next();
    return;
  }
  // Agent users should go to agent dashboard, not customer dashboard
  const isAgent = cookie.get(USER_TYPE) === 'agent';
  next(isAgent ? AGENT_DASHBOARD : CUSTOMER_DASHBOARD);
};

const ifAuthenticated = (_to, _from, next) => {
  if (store.getters[AUTH_AUTH_GETTER]) {
    next();
    return;
  }
  next(`/signin?r=${window.location.href}`);
};

const routes = [
  {
    path: '/',
    redirect: (to) => {
      const query = { ...(to.query || {}) };
      if (!store.getters[AUTH_AUTH_GETTER]) {
        return { path: '/signin', query };
      }
      const isAgent = cookie.get(USER_TYPE) === 'agent';
      if (isAgent) {
        return { path: AGENT_DASHBOARD };
      }
      // Referred customers land on products with agent still in the URL.
      if (query.agentCode) {
        return { path: '/buy/products', query };
      }
      return { path: CUSTOMER_DASHBOARD };
    },
  },
  {
    path: '/signin',
    name: 'SignIn',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/auth/SignIn'),
  },
  {
    path: '/signup',
    name: 'SignUp',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/auth/SignUp'),
  },
  {
    path: '/confirm-otp',
    name: 'ConfirmOtp',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/auth/ConfirmOTP'),
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/auth/password/ForgotPassword'),
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/auth/password/ResetPassword'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/Dashboard'),
  },
  {
    path: '/buy/products',
    name: 'CustomerProductSelect',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/CustomerProductSelect'),
  },
  {
    path: '/quote/products',
    name: 'GuestProductSelect',
    component: () => import('../views/guest/GuestProductSelect'),
  },
  {
    path: '/quote/motor/start',
    name: 'GuestMotorStart',
    component: () => import('../views/guest/GuestMotorStart'),
  },
  {
    path: '/quote/travel/plans',
    name: 'GuestTravelPlanSelect',
    component: () => import('../views/guest/GuestTravelPlanSelect'),
  },
  {
    path: '/quote/pa/plans',
    name: 'GuestPaPlanSelect',
    component: () => import('../views/guest/GuestPaPlanSelect'),
  },
  {
    path: '/buy/pa/plans',
    name: 'CustomerPaPlanSelect',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/pa/CustomerPaPlanSelect'),
  },
  {
    path: '/buy/pa/apply',
    name: 'CustomerPaSale',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/pa/CustomerPaSale'),
  },
  {
    path: '/buy/pa/complete/:quoteId',
    name: 'CustomerPaFinalization',
    beforeEnter: ifAuthenticated,
    props: true,
    component: () => import('../views/customer/pa/CustomerPaFinalization'),
  },
  {
    path: '/buy/travel/plans',
    name: 'CustomerTravelPlanSelect',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/travel/CustomerTravelPlanSelect'),
  },
  {
    path: '/buy/travel/sale',
    name: 'CustomerTravelSale',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/travel/CustomerTravelSale'),
  },
  {
    path: '/buy/travel/complete/:quoteId',
    name: 'CustomerTravelFinalization',
    beforeEnter: ifAuthenticated,
    props: true,
    component: () => import('../views/customer/travel/CustomerTravelFinalization'),
  },
  {
    path: '/buy/motor/start',
    name: 'CustomerMotorStart',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/motor/CustomerMotorStart'),
  },
  {
    path: '/buy/motor/apply',
    name: 'CustomerMotorApply',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/motor/CustomerMotorApply'),
  },
  {
    path: '/buy/motor/complete/:quoteId',
    name: 'CustomerMotorFinalization',
    beforeEnter: ifAuthenticated,
    props: true,
    component: () => import('../views/customer/motor/CustomerMotorFinalization'),
  },
  {
    path: '/agent-dashboard',
    name: 'AgentDashboard',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/AgentDashboard'),
  },
  {
    path: '/agent/quote',
    name: 'AgentProductSelect',
    beforeEnter: ifAuthenticated,
    component: () => import('@/views/agent/AgentProductSelect'),
  },
  {
    path: '/agent/underwriting',
    name: 'AgentUnderwriting',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentUnderwriting'),
  },
  {
    path: '/agent/sale',
    name: 'AgentSale',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentSale'),
  },
  {
    path: '/agent/pa/plans',
    name: 'AgentPaPlanSelect',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/pa/AgentPaPlanSelect'),
  },
  {
    path: '/agent/pa/sale',
    name: 'AgentPaSale',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/pa/AgentPaSale'),
  },
  {
    path: '/agent/pa/finalization/:quoteId',
    name: 'AgentPaFinalization',
    beforeEnter: ifAuthenticated,
    props: true,
    component: () => import('../views/agent/pa/AgentPaFinalization'),
  },
  {
    path: '/agent/finance',
    name: 'AgentFinance',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentFinance'),
  },
  {
    path: '/agent/finance/commissions',
    name: 'AgentCommissions',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentCommissions'),
  },
  {
    path: '/agent/finance/transactions',
    name: 'AgentTransactions',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentTransactions'),
  },
  {
    path: '/agent/finance/transactions/:transactionId',
    name: 'AgentTransactionDetails',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentTransactionDetails'),
  },
  {
    path: '/agent/reports',
    name: 'AgentReports',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentReports'),
  },
  {
    path: '/agent/settings',
    name: 'AgentSettings',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentSettings'),
  },
  {
    path: '/buy-cover',
    name: 'BuyCover',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/cover/BuyCover'),
  },
  {
    path: '/buy-cover/payment-response',
    name: 'PaymentResponse',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/cover/PaymentResponse'),
  },
  {
    path: '/request-for-quotation',
    name: 'Request',
    component: () => import('../views/rfq/Request'),
  },
  {
    path: '/travel-quote-summary',
    name: 'TravelQuoteSummary',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/TravelQuoteSummary'),
  },
  {
    path: '/travel/personal-details',
    name: 'PersonalDetailsTravel',
    component: () => import('../components/buy/travel/PersonalDetailsTravel'),
  },
  {
    path: '/travel/buy-for-someone-else',
    name: 'BuyForSomeoneElseCover',
    component: () => import('../views/BuyForSomeoneElseCover'),
  },
  {
    path: '/travel/non-resident-buy-cover',
    name: 'NonResidentTravelBuyCover',
    component: () => import('../views/NonResidentTravelBuyCover'),
  },
  {
    path: '/saved-quotes',
    name: 'SavedQuotes',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/quotes/SavedQuotes'),
  },
  {
    path: '/my-policies',
    name: 'MyPolicies',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/policies/MyPolicies'),
  },
  {
    path: '/my-renewals',
    name: 'CustomerRenewals',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/customer/CustomerRenewals'),
  },
  {
    path: '/my-renewals/:policyNo/renew',
    name: 'CustomerRenewPolicy',
    beforeEnter: ifAuthenticated,
    component: () => import('@/views/agent/AgentRenewPolicy'),
    props: { audience: 'customer' },
  },
  {
    path: '/my-policies/:policyNo',
    name: 'SinglePolicy',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/policies/SinglePolicy'),
  },
  {
    path: '/my-policies/:batchNo/renew/:policyNo',
    name: 'RenewPolicy',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/policies/RenewPolicy'),
  },
  {
    path: '/my-policies/:batchNo/pay-instalment/:policyNo',
    name: 'InstalmentPayment',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/policies/InstalmentPayment'),
  },
  {
    path: '/policy-result/:policyId/:type',
    name: 'PolicyResult',
    beforeEnter: ifAuthenticated,
    component: () => import('../components/policies/PolicyResult'),
    props: true,
  },
  {
    path: '/notifications',
    name: 'Notifications',
    beforeEnter: (to, from, next) => {
      // Temporarily hide customer notifications UI.
      next({ name: 'Dashboard', replace: true });
    },
  },
  {
    path: '/agent/renewals',
    name: 'AgentRenewals',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentRenewals'),
  },
  {
    path: '/agent/renewals/:policyNo',
    name: 'AgentRenewalDetails',
    beforeEnter: ifAuthenticated,
    component: () => import('@/views/agent/AgentRenewalDetails'),
  },
  {
    path: '/agent/renewals/:policyNo/renew',
    name: 'AgentRenewPolicy',
    beforeEnter: ifAuthenticated,
    component: () => import('@/views/agent/AgentRenewPolicy'),
  },
  {
    path: '/agent/travel/plans',
    name: 'AgentTravelPlanSelect',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/travel/AgentTravelPlanSelect'),
  },
  {
    path: '/agent/travel/sale',
    name: 'AgentTravelSale',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/travel/AgentTravelSale'),
  },
  {
    path: '/agent/travel/finalization/:quoteId',
    name: 'AgentTravelFinalization',
    beforeEnter: ifAuthenticated,
    props: true,
    component: () => import('../views/agent/travel/AgentTravelFinalization'),
  },
  {
    path: '/agent/motor/finalization/:quoteId',
    name: 'AgentMotorFinalization',
    beforeEnter: ifAuthenticated,
    props: true,
    component: () => import('../views/agent/AgentMotorFinalization'),
  },
  {
    path: '/agent/notifications',
    name: 'AgentMotorNotifications',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentMotorNotifications'),
  },
  {
    path: '/agent/conversions',
    name: 'AgentConversions',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentConversions'),
  },
  {
    path: '/agent/activity',
    name: 'AgentQuotes',
    alias: ['/agent/quotes', '/agent/active-quotes'],
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentQuotes'),
  },
  {
    path: '/agent/certificates',
    name: 'AgentCertificates',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentCertificates'),
    meta: { requiresAuth: true },
  },
  {
    path: '/agent/conversions/:policyNo',
    name: 'AgentConversionDetails',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentConversionDetails'),
  },
  {
    path: '/agent/claims/client-claims',
    name: 'AgentClientClaims',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentClientClaims'),
  },
  {
    path: '/agent/claims/client-claims/:claimId',
    name: 'AgentClientClaimDetails',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/agent/AgentClientClaimDetails'),
  },
  {
    path: '/claims',
    name: 'Claims',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/ClaimDetails'),
  },
  {
    path: '/claims/windscreen-form',
    name: 'WindscreenClaimForm',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/WindscreenClaimForm'),
  },
  {
    path: '/statement',
    name: 'ViewStatements',
    beforeEnter: ifAuthenticated,
    component: () => import('../views/ViewStatements'),
  },
  {
    path: '/providers',
    name: 'ViewProviders',
    component: () => import('../providers/ProviderHome'),
  },
  // ? last route for pwa default
  {
    path: '/index.html', // or '*' this is for PWA
    beforeEnter: (_to, _from, next) => {
      next('/');
    },
  },
  {
    path: '/error',
    name: 'ServerError',
    component: () => import('../views/errors/Error50x'),
  },
  {
    path: '/no-internet',
    name: 'NoInternet',
    component: Connection,
  },
  {
    path: '*',
    name: 'NotFound',
    component: () => import('../views/errors/Error404'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  // eslint-disable-next-line no-unused-vars
  scrollBehavior(to, _from, _savedPosition) {
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
  routes,
});

router.beforeEach(guardPortalAccess);

export default router;
